/*
 * Copyright (c) 2021. American Express Company
 */

// third-party
import React, { Component, ReactComponentElement } from 'react';
import { areEqual, FixedSizeList } from 'react-window';
import {
    DragDropContext,
    Draggable,
    DraggableProvided,
    DraggableRubric,
    DraggableStateSnapshot,
    Droppable,
    DropResult
} from 'react-beautiful-dnd';

// first-party
import { DraggableListItemModel, DraggableListViewModel } from 'src/types/view-models/draggable-list.view.model';
import { GlyphInfo, IconList, IconLock, TooltipButton, Checkbox } from '@americanexpress/dls-react';
import Typography from '../../components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import LiteralsService from 'src/services/literals/LiteralsService';

// icons and styles
import styles from './DraggableList.module.scss';
import Tooltip from 'src/components/dlsMods/tooltip/Tooltip';

export default class DraggableList extends Component<DraggableListViewModel, { items: DraggableListItemModel[] }> {
    public Row = React.memo((props: any) => {
        const { data: items, index, style } = props;
        const item = items[index];
        return (
            <Draggable draggableId={item.id.toString()} index={index} key={item.id}>
                {(provided) => this.getItem(provided, item, false, style)}
            </Draggable>
        );
    }, areEqual);

    public override state = {
        items: [] as DraggableListItemModel[]
    };

    constructor(props: any) {
        super(props);
        this.state = {
            items: this.props.items
        };
    }

    public override render(): ReactComponentElement<any> {
        return (
            <DragDropContext onDragEnd={this.onDragEnd.bind(this)}>
                <div className={styles.draggableListContainer}>
                    <Droppable
                        droppableId="droppable"
                        mode="virtual"
                        renderClone={(
                            provided: DraggableProvided,
                            snapshot: DraggableStateSnapshot,
                            rubric: DraggableRubric
                        ) => this.getItem(provided, this.state.items[rubric.source.index], snapshot.isDragging)}
                    >
                        {(provided) => (
                            <FixedSizeList
                                height={this.props.height || 500}
                                itemCount={this.state.items.length}
                                itemSize={this.props.itemHeight || 38}
                                width="100%"
                                style={{ borderRadius: '4px' }}
                                outerRef={provided.innerRef}
                                itemData={this.state.items}
                            >
                                {this.Row}
                            </FixedSizeList>
                        )}
                    </Droppable>
                </div>
            </DragDropContext>
        );
    }

    private static getStyle({ provided, style, isDragging }: any): unknown {
        return {
            ...style,
            ...provided.draggableProps.style
        };
    }

    private getItem(
        provided: DraggableProvided,
        item: DraggableListItemModel,
        isDragging?: boolean,
        style?: CSSStyleRule[]
    ): JSX.Element {
        const checkbox: JSX.Element =
            item.id === 0 ? (
                <div className={styles.dragLock}>
                    <IconLock />
                </div>
            ) : item.checked !== undefined ? (
                <div className={styles.dragCheck}>
                    <Checkbox checked={item.checked} onChange={(event: any) => this.onCheckChange(event, item.id)} />
                </div>
            ) : null;

        const tooltip: JSX.Element =
            item.id === 0 ? (
                <Tooltip
                    info
                    triggerElement={<TooltipButton info icon={<GlyphInfo />} />}
                    placement="bottom"
                    className="marginLeft"
                >
                    {LiteralsService.getLiteral('PlanForEducationCostsDrawer.baselineSavingsTooltip')}
                </Tooltip>
            ) : null;
        // special cases for free-cash
        const itemText = item.id === 0 ? 'Baseline Savings' : item.text;
        const itemSubtext = item.id === 0 ? 'used in all events' : item.subtext;

        const subtext: JSX.Element = itemSubtext ? (
            <Typography variant={TypographyVariants.LEGAL_PRIMARY} className={styles.dragSubtext}>
                {itemSubtext}
            </Typography>
        ) : null;
        return (
            <div
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                ref={provided.innerRef}
                style={DraggableList.getStyle({ provided, style, isDragging })}
                className={[
                    styles.draggableItem,
                    isDragging ? styles.draggableItemIsDragging : null,
                    item.subtext ? null : 'noSubtext'
                ].join(' ')}
            >
                <div className={styles.dragIcon}>
                    <IconList />
                </div>
                {checkbox}
                <div className={[styles.dragTextContainer, subtext ? styles.dragTextContainerCropped : null].join(' ')}>
                    <Typography variant={TypographyVariants.BODY} className={styles.dragText}>
                        {itemText}
                    </Typography>
                </div>
                {tooltip}
                <div className={styles.dragSubtextContainer}>{subtext}</div>
                <div className={styles.rowSpacer} />
            </div>
        );
    }

    private onCheckChange(event: any, id: number): void {
        const checked: boolean = event.target.checked;
        const items: DraggableListItemModel[] = this.state.items.map((item: DraggableListItemModel) =>
            item.id === id ? { ...item, checked } : item
        );
        this.onUpdate(items);
    }

    private onDragEnd(result: DropResult): void {
        if (!result.destination) return;
        if (result.source.index === result.destination.index) return;

        const items = DraggableList.reorder(this.state.items, result.source.index, result.destination.index);

        this.onUpdate(items);
    }

    private onUpdate(items: DraggableListItemModel[]): void {
        this.setState({ items });
        // send items back to client
        if (this.props.callback) this.props.callback(items);
    }

    private static reorder(
        list: Iterable<unknown> | ArrayLike<unknown>,
        startIndex: number,
        endIndex: number
    ): DraggableListItemModel[] {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result as DraggableListItemModel[];
    }
}
